import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { normalizeRoutePattern } from '../../utils/routePattern';
import { setClarityTag } from '../../hooks/useClarity';
import { trackPageView } from '../../utils/gtm';

/**
 * Route Tracker Component
 * Tracks all visited routes during your session and stores them in localStorage.
 * Use this to identify which pages are actually needed for MVP.
 *
 * Also the app's single analytics route hook: on every navigation it sends the
 * NORMALIZED route pattern (ids collapsed — e.g. /patients/:patientId) to
 * Clarity (custom tag `view`) and the GTM dataLayer (page_view). Dynamic URLs
 * fragmented per-view analytics into hundreds of one-off pages (502 distinct
 * /patients/<id> URLs in one 3-day Clarity window); filtering by the `view`
 * tag aggregates them. Never send raw ids here (PII rule — see useClarity.ts).
 *
 * To view tracked routes:
 * 1. Open browser console
 * 2. Run: JSON.parse(localStorage.getItem('visited_routes') || '[]')
 *
 * To export tracked routes:
 * Run in console: copy(JSON.parse(localStorage.getItem('visited_routes') || '[]'))
 *
 * To clear tracking:
 * Run in console: localStorage.removeItem('visited_routes')
 */
export const RouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const visited = JSON.parse(localStorage.getItem('visited_routes') || '[]');
    const currentPath = location.pathname;

    if (!visited.includes(currentPath)) {
      visited.push(currentPath);
      localStorage.setItem('visited_routes', JSON.stringify(visited));
      console.log(`[Route Tracker] Visited: ${currentPath} (Total: ${visited.length} unique routes)`);
    }
  }, [location]);

  useEffect(() => {
    const view = normalizeRoutePattern(location.pathname);
    // Null-safe: silently dropped in dev / before GTM+Clarity load in prod.
    setClarityTag('view', view);
    trackPageView(view);
  }, [location.pathname]);

  return null;
};

/**
 * Helper function to export visited routes as a formatted list
 * Call this in the browser console after your testing session
 */
(window as any).exportVisitedRoutes = () => {
  const visited = JSON.parse(localStorage.getItem('visited_routes') || '[]');
  const sorted = visited.sort();
  console.log('=== VISITED ROUTES ===');
  console.log(sorted.join('\n'));
  console.log(`\nTotal: ${sorted.length} unique routes`);
  return sorted;
};

/**
 * Helper function to clear tracking data
 */
(window as any).clearRouteTracking = () => {
  localStorage.removeItem('visited_routes');
  console.log('[Route Tracker] Cleared all tracking data');
};
